import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { CartLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { useCallback, useEffect, useRef } from 'react';
import { useDatalayerService } from './useDatalayerService';

interface UseCartDatalayerParams {
    request: IRequestContext;
    telemetry: ITelemetry;
    cartLines: CartLine[] | undefined;
    products: SimpleProduct[] | undefined;
    totalAmount: number | undefined;
}

interface UseCartDatalayerResult {
    trackApplyCoupon: () => void;
    trackGoToCheckout: () => void;
}

export const useCartDatalayer = ({
    request,
    telemetry,
    cartLines,
    products,
    totalAmount
}: UseCartDatalayerParams): UseCartDatalayerResult => {
    const datalayerService = useDatalayerService({ request, telemetry });
    const hasFiredRef = useRef(false);

    useEffect(() => {
        if (hasFiredRef.current || !cartLines?.length || !products?.length) {
            return;
        }
        hasFiredRef.current = true;
        datalayerService.pushViewCart(cartLines, products, totalAmount);
    }, [cartLines, products, totalAmount, datalayerService]);

    const trackApplyCoupon = useCallback(() => {
        datalayerService.pushApplyCoupon(cartLines ?? [], products ?? [], totalAmount);
    }, [datalayerService, cartLines, products, totalAmount]);

    const trackGoToCheckout = useCallback(() => {
        datalayerService.pushGoToCheckout(cartLines ?? [], products ?? [], totalAmount);
    }, [datalayerService, cartLines, products, totalAmount]);

    return { trackApplyCoupon, trackGoToCheckout };
};
